import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Checkbox, Menu } from '@mic3/platform-ui';
import genUuidv4 from 'uuid/v4';
import { Link } from 'react-router-dom';
import { baseRoutes } from 'app/config/routesConfig';

import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import AddLayerModal from '../AddLayerModal/AddLayerModal';
import olMap from './olMapController';
import MenuItem from 'app/components/molecules/Menu/MenuItem';
import { useToggle } from 'app/utils/hook/hooks';
import { additionalFields } from '../AddLayerModal/AdditionalFields';
import Button from 'app/components/atoms/Designer/Button';
import Container from 'app/components/atoms/Container/Container';
import styled from 'styled-components';
import { LAYERS, LAYERS_NAMES } from 'app/utils/maps/layer/layerUtils';
import { Typography } from '@material-ui/core';
import { generateAvailableLayers } from 'app/utils/maps/layer/layerUtils';

const layerTypes = [
    {
        name: LAYERS_NAMES.wms,
        icon: 'layers',
        uri: LAYERS.wms,
    },
    {
        name: LAYERS_NAMES.xyz,
        icon: 'view-grid',
        uri: LAYERS.xyz,
    },
];

const ButtonContainerStyled = styled(Container)`
    max-width: 960px;
    display: flex;
    align-items: center;
    padding: 1rem 0;
`;

const StyledHeader = styled(Typography)`
    font-size: 18px !important;
    line-height: 28px !important;
    font-weight: 500 !important;
`;

const NavigationLink = styled(Link)`
    color: ${({ theme }) => theme.material.colors.primary.main};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    
    &:hover {
        text-decoration: underline;
    }
`;

const InfoText = styled.div`
    color: ${({ theme }) => theme.material.colors.text.secondary};
    font-size: 14px;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    border-radius: 4px;
`;

const CheckboxLabel = styled.div`
    color: ${({ theme }) => theme.material.colors.text.secondary};
    margin-left: -8px;
`;

const CheckboxTitle = styled.div`
    font-size: 16px;
    font-weight: 500;
`;

const BaseLayersAdminSection = ({ onChange, data, formRef, isUnCollapsed, addNewBaseLayer, setFormState, formRevertKey, canEdit }) => {
    const [selectedLayerIndex, setSelectedLayerIndex] = useState(-1);
    const [selectedMenu, setSelectedMenu] = useState();
    const [isOpen, toggleMenu] = useToggle(false);
    const anchorEl = useRef(null);

    const selectedLayerData = data?.baseLayers?.[selectedLayerIndex];

    const map = useMemo(() => new olMap(), []);

    const generatedLayersOptions = useMemo(() => {
        return generateAvailableLayers(data);
    }, [data]);

    const removeLayer = useCallback(
        (baseLayer) => {
            setFormState((pre) => {
                const filteredLayers = pre.baseLayers.filter((bl) => bl.id !== baseLayer.id);
                const newState = { ...pre, baseLayers: filteredLayers };
                if(baseLayer?.name === newState?.defaultBaseLayer) { // if default selected layer removed
                    newState.defaultBaseLayer = null;
                }
                return newState;
            });
        },
        [setFormState]
    );

    const onMenuClick = (name, uri) => {
        setSelectedMenu({ name, uri });
        toggleMenu(false);
    };

    const onAddLayer = (formData) => {
        if (selectedLayerData) {
            const updatedLayers = data.baseLayers.map((bl) => {
                if (bl.id === formData.id) {
                    return formData;
                }
                return bl;
            });
            if(data?.defaultBaseLayer === selectedLayerData?.name && selectedLayerData?.name !== formData?.name) { // if default selected layer renamed
                setFormState({ ...data, baseLayers: updatedLayers, defaultBaseLayer: null });
            } else {
                setFormState({ ...data, baseLayers: updatedLayers });
            }
        } else {
            addNewBaseLayer && addNewBaseLayer({ ...formData, id: genUuidv4(), layerType: selectedMenu.uri });
        }
    };

    const formDefinitions = useMemo(() => {
        return [
            {
                type: 'panel',
                properties: {
                    header: 'Enabled Built-in Base Layers',
                    collapsible: false,
                },
                children: [
                    {
                        type: 'custom',
                        properties: {
                            name: 'enableBuiltInBaseLayers',
                            noBackground: true,
                            defaultValue: true,
                            Component: (props) => (
                                <>
                                    <CheckboxContainer>
                                        <Checkbox disabled={!canEdit} name={props.name} value={props.value} onClick={props.onChange} />
                                        <CheckboxLabel>
                                            <CheckboxTitle>Open Street Maps</CheckboxTitle>
                                        </CheckboxLabel>
                                    </CheckboxContainer>
                                </>
                            ),
                        },
                    },
                ],
            },
            {
                type: 'panel',
                properties: {
                    header: 'Google Maps',
                    collapsible: false,
                },
                children: [
                    {
                        type: 'custom',
                        properties: {
                            name: 'enableGoogleMaps',
                            noBackground: true,
                            defaultValue: true,
                            Component: (props) => (
                                <>
                                    <CheckboxContainer>
                                        <Checkbox disabled={!canEdit} name={props.name} value={props.value} onClick={props.onChange} />
                                        <CheckboxLabel>
                                            <CheckboxTitle>Google Maps</CheckboxTitle>
                                        </CheckboxLabel>
                                    </CheckboxContainer>
                                </>
                            ),
                        },
                    },
                    {
                        type: 'text',
                        properties: {
                            name: 'feGoogleApiKey',
                            label: 'JavaScript API key',
                        },
                        constraints: {
                            required: ({ enableGoogleMaps }) => !!enableGoogleMaps,
                        },
                    },
                ],
            },
            {
                type: 'panel',
                properties: {
                    header: 'Maps Module Settings',
                    collapsible: false,
                },
                children: [
                    {
                        type: 'custom',
                        properties: {
                            cid: 'Base Layer Settings Link',
                            noBackground: true,
                            Component: () => {
                                return (
                                    <InfoText>
                                        Change map module specific settings <NavigationLink to={baseRoutes['admin-maps']}>here</NavigationLink>.
                                    </InfoText>
                                );
                            },
                        },
                    },
                ],
            },
            {
                type: 'panel',
                properties: {
                    header: 'Base Layers',
                    collapsible: false,
                },
                children: [
                    {
                        type: 'typeahead',
                        properties: {
                            name: 'defaultBaseLayer',
                            label: 'Default base layer',
                            options: generatedLayersOptions,
                            defaultValue: 'OSM',
                        },

                    },
                    {
                        type: 'button',
                        properties: {
                            name: 'add_base_layer',
                            label: 'Add Base Layer',
                            noBackground: true,
                            fullWidth: false,
                            iconName: 'plus',
                            onClick: toggleMenu,
                            buttonRef: anchorEl,
                        },
                    },
                ].filter(Boolean),
            },
        ];
    }, [toggleMenu, generatedLayersOptions, canEdit]);

    const baseLayerComponents = useMemo(() => {
        if (!data?.baseLayers?.length) return null;
        return data?.baseLayers?.map((layerData, i) => {
            return (
                <FormGenerator
                    key={`${layerData?.id}-${i}`}
                    data={layerData}
                    disabled={true}
                    components={[
                        {
                            type: 'panel',
                            properties: {
                                header: (
                                    <StyledHeader>
                                        {LAYERS_NAMES[layerData?.layerType]} : {layerData.name}
                                    </StyledHeader>
                                ),
                                expanded: isUnCollapsed,
                            },
                            children: [
                                {
                                    field: 'name',
                                    type: 'text',
                                    properties: { label: 'Layer name', name: 'name' },
                                },
                                ...additionalFields(LAYERS_NAMES[layerData?.layerType], layerData),
                                {
                                    type: 'custom',
                                    properties: {
                                        name: 'removeButton',
                                        Component: () => (
                                            <ButtonContainerStyled>
                                                <Button
                                                    variant='text'
                                                    hexColor='#7B9EEA'
                                                    iconName='pencil'
                                                    onClick={() => {
                                                        setSelectedLayerIndex(i)}
                                                    }
                                                    disabled={!canEdit}
                                                >
                                                    Edit Base Layer
                                                </Button>
                                                <Button
                                                    variant='text'
                                                    hexColor='#7B9EEA'
                                                    iconName='trash-can'
                                                    onClick={() => removeLayer(layerData)}
                                                    disabled={!canEdit}
                                                >
                                                    Remove Base Layer
                                                </Button>
                                            </ButtonContainerStyled>
                                        ),
                                    },
                                },
                            ],
                        },
                    ]}
                />
            );
        });
    }, [data, removeLayer, isUnCollapsed, canEdit]);

    return (
        <>
            <FormGenerator
                disabled={!canEdit}
                key={formRevertKey}
                onChange={onChange}
                ref={formRef}
                data={data}
                components={formDefinitions}
            />
            <Menu open={isOpen} onClose={toggleMenu} anchorEl={anchorEl.current}>
                {layerTypes.map(({ name, icon, uri, iconType }) => (
                    <MenuItem key={uri} onClick={() => onMenuClick(name, uri)} name={name} icon={icon} iconType={iconType || 'mdi'} />
                ))}
            </Menu>
            {(selectedLayerData || selectedMenu) ? (
                <AddLayerModal
                    layerData={selectedLayerData}
                    layer={selectedMenu ? selectedMenu : layerTypes.find((layer) => layer?.uri === selectedLayerData?.layerType)}
                    map={map}
                    mapId='add-layer-map'
                    closeModal={() => {
                        setSelectedLayerIndex(-1)
                        setSelectedMenu(null);
                    }}
                    onSubmit={onAddLayer}
                    layersList={data?.baseLayers}
                />
            ) : null}
            {baseLayerComponents}
        </>
    );
};
export default BaseLayersAdminSection;